<script setup lang='ts'>
  import { computed } from 'vue'
  import { useQuery } from '@tanstack/vue-query'

  import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
  import { mapCmsContent } from '@/utils'
  import { useCmsContentSearch } from '@/composables/useCmsContentSearch'
  import { useSubjectResource } from '@/composables/useSubjectResource'
  import type { Item } from '@/types/Ibexa'

  import AssignmentCollectionPage from '@/components/pages/AssignmentCollectionPage.vue'
  import FocusTaskPage from '@/components/pages/FocusTaskPage.vue'
  import Presentation from '@/components/presentation/Presentation.vue'
  import VgsSpinner from '@/components/common/VgsSpinner.vue'

  const { search } = useCmsContentSearch()
  const { subjectResource } = useSubjectResource()

  const props = defineProps<{
    contentId: number
  }>()

  const parentLocationId = computed(() => subjectResource.value?.locationId as string)
  const subjectResourceSearchQuery = computed(() => ({
    ['parentLocationId[]']: [parentLocationId.value],
    ['contentTypes[]']: [
      IbexaContentTypeIdentifier.vgsFlytTask,
      IbexaContentTypeIdentifier.vgsFolder,
      IbexaContentTypeIdentifier.vgsPresentation,
      IbexaContentTypeIdentifier.vgsStepTask,
    ],
    limit: '1',
  }))

  const { data: childData, isLoading } = useQuery({
    queryKey: ['subjectResource', props.contentId],
    queryFn: () => search(subjectResourceSearchQuery.value),
    enabled: computed(() => !!parentLocationId.value),
    staleTime: Infinity
  })

  const getComponentName = (contentTypeIdentifier: IbexaContentTypeIdentifier) => {
    switch (contentTypeIdentifier) {
    case IbexaContentTypeIdentifier.vgsStepTask:
    case IbexaContentTypeIdentifier.vgsFlytTask:
      return FocusTaskPage
    case IbexaContentTypeIdentifier.vgsFolder:
      return AssignmentCollectionPage
    case IbexaContentTypeIdentifier.vgsPresentation:
      return Presentation
    default:
      console.warn(`No component found for content type identifier: ${contentTypeIdentifier}`)
      return
    }
  }

  const mappedChildData = computed(() => childData.value?.map((item: Item) => mapCmsContent(item))[0])
  const contentTypeIdentifier = computed(() => mappedChildData.value ? mappedChildData.value?.contentTypeIdentifier : null)
  const isFocusTask = computed(() => contentTypeIdentifier.value === IbexaContentTypeIdentifier.vgsFlytTask || contentTypeIdentifier.value === IbexaContentTypeIdentifier.vgsStepTask)
</script>

<template>
  <VgsSpinner :is-loading="isLoading" />

  <template v-if="!isLoading && contentTypeIdentifier">
    <component
      :is="getComponentName(contentTypeIdentifier)"
      v-if="mappedChildData"
      :element="isFocusTask ? contentId : mappedChildData"
    />
  </template>
</template>
